/**
 * Store paths — manifest-only catalog living at `~/.skaile/store/`.
 *
 * The store is symmetric to a remote catalog (skaile.store) but lives on the
 * user's machine. It holds *manifests* (pointer triples) only — never bytes.
 * Bytes live under `~/.skaile/cache/sources/` (third-party clones) or
 * `~/.skaile/libraries/<name>/` (user authoring).
 *
 * Layout:
 * ```
 * ~/.skaile/store/
 * ├── config.yaml          mode: auto|local|remote, ttl, …
 * ├── favourites.yaml      user pins
 * ├── manifests/           catalog entries (one yaml per ref)
 * ├── remote-cache/        TTL cache of remote-store responses (.gitignore'd)
 * └── .git/                store is a git repo (push to team remote)
 * ```
 *
 * @docLink packages/library/concepts#store-paths
 */

import { spawnSync } from "node:child_process";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";

import { getSidecarRoot } from "./sidecar-paths.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve the absolute path to the local store root (`~/.skaile/store/`).
 *
 * Honors `SKAILE_HOME` (test isolation hook), mirroring {@link getSidecarRoot}.
 */
export function getStoreRoot(): string {
  const home = process.env.SKAILE_HOME ?? path.join(os.homedir(), ".skaile");
  return path.join(home, "store");
}

/** Manifests subdir: `<store>/manifests/`. */
export function getStoreManifestsDir(): string {
  return path.join(getStoreRoot(), "manifests");
}

/** Marker that signals the sidecar→store migration has run. */
function migratedMarkerPath(): string {
  return path.join(getStoreRoot(), ".migrated");
}

/**
 * Build the candidate list before touching the filesystem. Prefer the
 * current `skaile.manifest.yaml`; fall back to the legacy `.skaile-source.yaml`
 * so sidecars written before the rename still migrate.
 */
function collectSidecarMigrationCandidates(sidecarRoot: string): { src: string; slug: string }[] {
  const candidates: { src: string; slug: string }[] = [];
  if (fs.existsSync(sidecarRoot)) {
    for (const entry of fs.readdirSync(sidecarRoot, { withFileTypes: true })) {
      if (!entry.isDirectory()) continue;
      if (entry.name.startsWith(".")) continue;
      const sidecarDir = path.join(sidecarRoot, entry.name);
      const current = path.join(sidecarDir, "skaile.manifest.yaml");
      const legacy = path.join(sidecarDir, ".skaile-source.yaml");
      const src = fs.existsSync(current) ? current : fs.existsSync(legacy) ? legacy : undefined;
      if (src) candidates.push({ src, slug: entry.name });
    }
  }
  return candidates;
}

/**
 * Move each candidate manifest into `manifestsDir`. Returns the number moved.
 * Per-candidate failures are swallowed and logged to stderr; destinations that
 * already exist (from a prior partial run) are skipped.
 */
function moveSidecarManifests(
  candidates: { src: string; slug: string }[],
  manifestsDir: string,
): number {
  let migrated = 0;
  for (const { src, slug } of candidates) {
    const dest = path.join(manifestsDir, `${slug}.yaml`);
    if (fs.existsSync(dest)) continue; // already migrated by a prior partial run
    try {
      fs.renameSync(src, dest);
      migrated++;
    } catch (err) {
      process.stderr.write(
        `[skaile/store] Failed to migrate sidecar ${slug}: ${err instanceof Error ? err.message : String(err)}\n`,
      );
    }
  }
  return migrated;
}

/**
 * Initialise the store as a git repo on first move so users can push it as
 * a team store. Failure is non-fatal — the marker still gets written so we
 * don't retry on every open.
 */
function initStoreGitRepo(storeRoot: string): void {
  if (fs.existsSync(path.join(storeRoot, ".git"))) return;
  try {
    spawnSync("git", ["init", "--quiet"], { cwd: storeRoot, stdio: "ignore" });
    fs.writeFileSync(
      path.join(storeRoot, ".gitignore"),
      "# Remote-store cache — local to this machine.\nremote-cache/\n",
      "utf8",
    );
  } catch {
    // Ignore — git may not be available; the directory is still usable.
  }
}

// ---------------------------------------------------------------------------
// One-shot sidecar → store/manifests migration
// ---------------------------------------------------------------------------

/**
 * Move every `~/.skaile/sidecars/<slug>/skaile.manifest.yaml` (or the legacy
 * `.skaile-source.yaml` for sidecars minted before the file-split rename) into
 * `~/.skaile/store/manifests/<slug>.yaml`, initialise the store as a git repo,
 * and write a marker so the migration is a no-op on subsequent runs.
 *
 * Idempotent. Cheap to call (single `existsSync` for the marker on the hot
 * path). Errors are swallowed and logged to stderr — a partial migration
 * leaves the marker absent so the next run retries.
 */
export function migrateSidecarsToStoreIfNeeded(): { migrated: number; storeRoot: string } {
  const storeRoot = getStoreRoot();
  const marker = migratedMarkerPath();
  if (fs.existsSync(marker)) return { migrated: 0, storeRoot };

  const sidecarRoot = getSidecarRoot();
  const manifestsDir = getStoreManifestsDir();

  const candidates = collectSidecarMigrationCandidates(sidecarRoot);

  fs.mkdirSync(manifestsDir, { recursive: true });

  const migrated = moveSidecarManifests(candidates, manifestsDir);

  initStoreGitRepo(storeRoot);

  fs.writeFileSync(marker, new Date().toISOString(), "utf8");
  return { migrated, storeRoot };
}
